// Shared multi-select state for list pages (Phase 1.13 — bulk actions).
// One hook, reused on Tasks, Finance (invoices + expenses) and Leads list
// view, rather than a per-page copy. Selection auto-prunes whenever the
// visible id set changes (filter/tab switch, or a bulk action removing rows)
// so a stale id can never linger selected off-screen.

import { useCallback, useEffect, useMemo, useState } from 'react';

export function useBulkSelect(visibleIds: readonly string[]) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(visibleIds);
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleIds]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);
  const isSelected = useCallback((id: string) => selected.has(id), [selected]);
  const ids = useMemo(() => [...selected], [selected]);

  return { selected, ids, count: selected.size, toggle, clear, isSelected };
}

export type BulkSelect = ReturnType<typeof useBulkSelect>;
